/* eslint-disable @typescript-eslint/no-explicit-any */
import { memo, useEffect, useMemo, useState } from 'react';
import { Box, Grid, IconButton, Stack } from '@mui/material';
import PageMainTitle from 'components/cmn/PageMainTitle';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useAppDispatch } from 'store';
import isEmpty from 'lodash/isEmpty';
import { showMessage } from 'store/message/messageSlice';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { IDiagnosticManagementStateProps, SurveyListLatestContent } from 'interface/project/projectInfo';
import DiagnosticLayout from 'components/diagnostic-management/id/DiagnosticLayout';
import DiagnosticForm from 'components/diagnostic-management/id/DiagnosticForm';
import { closeBackdrop, openBackdrop } from 'store/backdrop/backdropSlice';
import { postSurveyQuestionUpload, postSurveySave } from 'store/project/projectSlice';
import { useProject } from 'hooks/useProject';

function MemberProjectManagementPage(): JSX.Element {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { getInfiniteSurveyListLatest } = useProject();
  const { mutate } = getInfiniteSurveyListLatest();
  const { id } = useParams();
  const [formData, setFormData] = useState<IDiagnosticManagementStateProps>({
    surveyName: '',
    surveyType: '',
    surveyDesc: '',
    surveyFileName: {
      saveFileName: '',
      file: null,
      rowCount: 0,
      excelData: [],
    },
  });

  const dataInfo = useMemo(
    () => (location?.state?.dataInfo as SurveyListLatestContent) || {},
    [location?.state?.dataInfo],
  );

  useEffect(() => {
    if (isEmpty(id) || Object.keys(dataInfo).length === 0) {
      dispatch(showMessage({ message: '부적절한 경로로 접근하였습니다. 다시 시도해주세요.', variant: 'warning' }));
      navigate('/admin/diagnostic-management');
    } else {
      setFormData(prevState => ({
        ...prevState,
        surveyName: dataInfo.surveyName,
        surveyType: dataInfo.surveyType,
        surveyDesc: dataInfo.surveyDesc,
        surveyFileName: {
          ...prevState.surveyFileName,
          saveFileName: dataInfo.saveFileName,
        },
      }));
    }
  }, [dataInfo, dispatch, id, navigate]);

  const handlePrev = () => {
    navigate('/admin/diagnostic-management');
  };

  const handleFileReset = () => {
    setFormData(prevState => ({
      ...prevState,
      surveyFileName: {
        file: null,
        rowCount: 0,
        excelData: [],
        saveFileName: '',
      },
    }));
  };
  const handleFileChange = ({ file, rows }: { file: File; rows: any[][] }) => {
    setFormData(prevState => ({
      ...prevState,
      surveyFileName: {
        file,
        rowCount: rows.length,
        excelData: rows,
        saveFileName: file.name,
      },
    }));
  };
  const handleValidCheck = () => {
    if (!formData.surveyName) {
      return '진단명을 입력해주세요.';
    }
    if (!formData.surveyFileName.file) {
      return '진단 파일을 업로드해주세요.';
    }
    if (!formData.surveyType) {
      return '진단 유형을 선택해주세요.';
    }
    if (!formData.surveyDesc) {
      return '수정 내역을 입력해주세요.';
    }
    return '';
  };
  const handleSave = () => {
    if (!isEmpty(handleValidCheck())) {
      dispatch(showMessage({ message: handleValidCheck(), variant: 'warning' }));
      return;
    }
    dispatch(openBackdrop({}));
    dispatch(
      postSurveySave({
        surveyName: formData.surveyName,
        saveFileName: formData.surveyFileName.file?.name || '',
        surveyDesc: formData.surveyDesc,
        surveyType: formData.surveyType,
      }),
    )
      .unwrap()
      .then(response => {
        const { status, data } = response;
        if (status === 200) {
          const surveyId = data.result;
          if (surveyId) {
            handleQuestionUpload(surveyId);
          } else {
            dispatch(closeBackdrop());
          }
        } else {
          dispatch(closeBackdrop());
          dispatch(showMessage({ message: response?.data?.message || '실패하였습니다.', variant: 'warning' }));
        }
      })
      .catch(e => {
        console.log(e);
        dispatch(closeBackdrop());
        dispatch(showMessage({ message: '문항 등록 중 오류가 발생했습니다.', variant: 'error' }));
      });
  };
  const handleQuestionUpload = (surveyId: string) => {
    dispatch(
      postSurveyQuestionUpload({
        surveyId,
        file: formData.surveyFileName.file as File,
      }),
    )
      .unwrap()
      .then(response => {
        const { status } = response;
        if (status === 200) {
          dispatch(closeBackdrop());
          dispatch(showMessage({ message: '문항 등록이 완료되었습니다.', variant: 'success' }));
          mutate();
          navigate('/admin/diagnostic-management');
        } else {
          dispatch(closeBackdrop());
          dispatch(showMessage({ message: '문항 등록 중 오류가 발생했습니다.', variant: 'error' }));
        }
      })
      .catch(e => {
        console.log(e);
        dispatch(closeBackdrop());
        dispatch(showMessage({ message: '문항 등록 중 오류가 발생했습니다.', variant: 'error' }));
      });
  };

  const type = 'modify';
  return (
    <DiagnosticLayout onSave={handleSave} onPrev={handlePrev} type={type}>
      <Grid item xs={12} sx={{ mb: 0 }}>
        <Stack direction="row" spacing={1}>
          <IconButton onClick={handlePrev} sx={{ width: 32 }}>
            <ArrowBackIcon />
          </IconButton>
          <PageMainTitle title="진단 정보 수정" />
        </Stack>
      </Grid>
      <Grid item xs={12}>
        <Box sx={{ px: '1.625rem' }}>
          <Grid
            container
            spacing={2}
            sx={{
              mt: '1rem',
              width: '100%',
              display: 'flex',
              justifyContent: { xs: 'flex-start', lg: 'center' },
            }}
          >
            <DiagnosticForm
              dataInfo={dataInfo}
              formData={formData}
              setFormData={setFormData}
              handleFileChange={handleFileChange}
              handleFileReset={handleFileReset}
              id={id || ''}
              type={type}
            />
          </Grid>
        </Box>
      </Grid>
    </DiagnosticLayout>
  );
}

export default memo(MemberProjectManagementPage);
